import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, isAbort } from '../shell/bridge';
import { FIRST_ACT_KEY } from '../components/FirstAct';

/**
 * InterestsNudgeToast — card mMl44G43YK5X.
 *
 * "Your interests make the concierge smarter — 2 min": a low-commitment nudge
 * toward the interests questionnaire, shown after the first login as a bottom
 * toast — never a modal takeover, because the quiz is low-commitment and the
 * nudge should be too (Jonathan 2026-10-14).
 *
 * Three rules from the card:
 *   1. it belongs to the FIRST-login moment only. Eligibility is read once at
 *      mount, while the first-act card is still un-dealt (FIRST_ACT_KEY unset)
 *      and the tour unseen — so a person who already onboarded, or who is on
 *      their fifth login, never meets it;
 *   2. it waits for the first-login overlays (pair ceremony, first-act card,
 *      the tour) to clear and for a three-second grace, so it never competes
 *      for that slot; it also hides the moment the person navigates anywhere;
 *   3. it links straight to the quiz — #settings-you opens Settings on the You
 *      section where "Your interests" lives (the same deep link Digest uses) —
 *      and it never re-nags: the profile is re-read on mount (a completed
 *      profile silences it for good), and localStorage caps it at two
 *      appearances and two dismissals, after which it is gone for good.
 */

/** localStorage: { shown: n, dismissed: n } — the nudge's whole memory. */
export const NUDGE_KEY = 'pear.interests-nudge';

/** Deep link to Settings → You, where the interests quiz lives. */
export const INTERESTS_HASH = '#settings-you';

/** How long the toast waits after arrival before it may appear (ms). */
const GRACE_MS = 3000;
/** Give up on this page load if the slot never frees (ms). */
const GIVEUP_MS = 180000;
/** Appearances and dismissals are each capped — see NUDGE_KEY. */
const SHOW_CAP = 2;
const DISMISS_CAP = 2;

interface NudgeState {
  shown: number;
  dismissed: number;
}

function readState(): NudgeState {
  try {
    const raw = localStorage.getItem(NUDGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<NudgeState>) : null;
    return {
      shown: Number(parsed?.shown) || 0,
      dismissed: Number(parsed?.dismissed) || 0,
    };
  } catch {
    // Storage unreadable — assume the caps are spent and never nag.
    return { shown: SHOW_CAP, dismissed: DISMISS_CAP };
  }
}

function writeState(state: NudgeState) {
  try {
    localStorage.setItem(NUDGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode — it will offer itself again, which is the intent */
  }
}

/** True while something else owns the first-login moment. */
function slotBusy(): boolean {
  return Boolean(
    document.getElementById('pair-ceremony') ||
      document.getElementById('first-act') ||
      document.getElementById('onboarding-panel'),
  );
}

/** One appearance per page load, module-scoped so a remount cannot re-show. */
let shownThisLoad = false;

export function InterestsNudgeToast() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Eligibility is the first-login period: the first-act card has not been
    // dealt and the tour has not been taken on THIS device yet. Read once, at
    // mount — dismissing the first-act later in the same load does not
    // re-open eligibility on the next one.
    let firstLogin = false;
    try {
      firstLogin =
        localStorage.getItem(FIRST_ACT_KEY) !== '1' &&
        localStorage.getItem('pear.onboarded') !== '1';
    } catch {
      firstLogin = false;
    }
    if (!firstLogin || shownThisLoad) return;

    let alive = true;
    let completed: boolean | null = null; // null = still reading the profile
    api<{ interests: { completed: boolean } | null }>('/api/interests')
      .then(d => {
        if (alive) completed = Boolean(d?.interests?.completed);
      })
      .catch(err => {
        // A torn-down request is not an answer; anything else means the read
        // failed, and an unread profile should not silence the nudge.
        if (alive && !isAbort(err)) completed = false;
      });

    const started = Date.now();
    let timer: number | undefined;
    const tick = () => {
      if (!alive) return;
      const state = readState();
      if (
        state.dismissed >= DISMISS_CAP ||
        state.shown >= SHOW_CAP ||
        completed === true
      ) {
        return; // never re-nag
      }
      if (completed === null || slotBusy()) {
        if (Date.now() - started > GIVEUP_MS) return;
        timer = window.setTimeout(tick, 500);
        return;
      }
      if (Date.now() - started < GRACE_MS) {
        timer = window.setTimeout(tick, GRACE_MS - (Date.now() - started));
        return;
      }
      shownThisLoad = true;
      writeState({ ...state, shown: state.shown + 1 });
      setShow(true);
    };
    timer = window.setTimeout(tick, GRACE_MS);

    // Navigating anywhere ends the moment: the nudge does not follow the
    // person around the app, and Settings (where the quiz lives) is already
    // saying the same thing with a form.
    const onNav = () => setShow(false);
    window.addEventListener('hashchange', onNav);

    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('hashchange', onNav);
    };
  }, []);

  const start = () => {
    setShow(false);
    window.location.hash = INTERESTS_HASH;
  };

  const dismiss = () => {
    const state = readState();
    writeState({ ...state, dismissed: state.dismissed + 1 });
    setShow(false);
  };

  if (!show) return null;

  /* Reuses the app's bottom-toast recipe (.pear-update-toast) via a portal to
     body — a transformed ancestor is the containing block for fixed
     positioning, so the portal keeps it pinned. */
  return createPortal(
    <div className="pear-update-toast" role="status" data-testid="interests-nudge">
      <span className="pear-update-toast-text">
        Your interests make the concierge smarter — 2 min.
      </span>
      <button
        type="button"
        className="pear-interests-nudge-start"
        id="btn-interests-nudge-start"
        onClick={start}
      >
        Start the quiz
      </button>
      <button
        type="button"
        className="pear-update-toast-dismiss"
        id="btn-interests-nudge-dismiss"
        aria-label="Dismiss interests reminder"
        onClick={dismiss}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
